import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { NotificationFeed } from "./NotificationFeed";
import { NotificationRowSkeleton } from "./NotificationRowSkeleton";
import { NOTIFICATION_EMPTY_STATES } from "./utils";
import { useNotificationActionMutation } from "../api/useNotificationActionMutation";
import { useNotificationsPage } from "../api/useNotificationsPage";
import type { NotificationAction } from "../constants";
import { useIntegrationNames } from "../hooks/useIntegrationNames";
import { useNotificationRouteAccess } from "../hooks/useNotificationRouteAccess";
import type { NotificationActionOptions, NotificationData, NotificationFilters } from "../types";
import { flattenNotificationPages, getNotificationLinks } from "../utils";

const SCROLL_TARGET_ID = "notification-scroll-target";

type NotificationBellContentProps = {
  filters: NotificationFilters;
  onNavigate: () => void;
};

export function NotificationBellContent({ filters, onNavigate }: NotificationBellContentProps) {
  const { t } = useTranslation();
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useNotificationsPage(filters);
  const notifications = useMemo(() => flattenNotificationPages(data?.pages), [data]);
  const notificationAction = useNotificationActionMutation();
  const routeAccess = useNotificationRouteAccess();
  const integrationNames = useIntegrationNames();

  const handleAction = (
    notificationId: NotificationData["id"],
    action: NotificationAction,
    options?: NotificationActionOptions,
  ) => {
    notificationAction.mutate({
      action,
      target: { notificationIds: [notificationId] },
      keepInView: options?.keepInView ? filters : undefined,
    });
  };

  if (isLoading) {
    return (
      <div className="divide-y" aria-busy="true">
        <NotificationRowSkeleton />
        <NotificationRowSkeleton />
        <NotificationRowSkeleton />
      </div>
    );
  }

  if (notifications.length === 0) {
    const emptyState = NOTIFICATION_EMPTY_STATES[filters.status];
    return (
      <div className="flex flex-col items-center gap-1 px-4 py-8 text-center">
        <p className="text-sm font-medium">{t(emptyState.title)}</p>
        <p className="text-sm text-muted-foreground">{t(emptyState.description)}</p>
      </div>
    );
  }

  return (
    <NotificationFeed
      surface="popover"
      scrollTargetId={SCROLL_TARGET_ID}
      notifications={notifications}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      onOpen={onNavigate}
      onAction={handleAction}
      getRowHref={(notification) => getNotificationLinks(notification, routeAccess, integrationNames).primary?.to}
    />
  );
}
